import {
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "components/ui/drawer";
import { Button } from "./ui/button";
import { observer } from "mobx-react-lite";
import cart from "observables/cart";
import pluralize from "pluralize";
import orderBy from "lodash/orderBy";
import { Trash2, Minus, Plus, Pen, Check } from "lucide-react";
import { Card } from "./ui/card";
import { useState } from "react";

const CustomerCartDrawer = ({
  onClickEmptyCart,
  onClickNext,
  onClickRemove,
  onClickRemoveCombo,
  isReadOnly,
}: {
  onClickEmptyCart?: () => void;
  onClickNext: () => void;
  onClickRemove?: (variantId: number) => void;
  onClickRemoveCombo?: (variantIds: Array<number>) => void;
  onClickAddCombo?: () => void;
  isReadOnly?: boolean;
}) => {
  const [editingItemId, setEditingItemId] = useState<number | null>(null);
  const [editingQuantity, setEditingQuantity] = useState<number>(1);

  const handleRemoveExtra = (extraId: number) => () => {
    cart.removeExtra(extraId);
  };

  const handleClickEmptyCart = () => {
    cart.reset();
    onClickEmptyCart?.();
  };

  const handleRemoveCombo = (
    comboKey: number,
    comboVariantIds: Array<number>
  ) => {
    // The issue is that comboKey is not the actual key in cart.combos
    // We need to find the correct key from the combos object
    const comboKeys = Object.keys(cart.combos);
    // Find the combo key that contains the combo with the given comboId
    const actualComboKey = comboKeys.find((key) => {
      const combo = cart.combos[key];
      return combo.some((item) => item.comboId === comboKey);
    });

    if (actualComboKey) {
      cart.removeCombo(parseInt(actualComboKey));
      onClickRemoveCombo?.(comboVariantIds);
    }
  };

  const startEditingItem = (item: any) => {
    setEditingItemId(item.id);
    setEditingQuantity(item.quantity);
  };

  const updateItemQuantity = (item: any) => {
    // Find the item in the cart
    const cartItem = cart.items.find((i) => i.id === item.id);
    if (cartItem) {
      // Update the quantity
      cartItem.quantity = editingQuantity;
      // Update the amount
      cartItem.amountAfterVat =
        editingQuantity * Number(cartItem.variant.priceAfterVat);

      // Save to localStorage
      localStorage.setItem("items", JSON.stringify(cart.items));
    }

    // Exit editing mode
    setEditingItemId(null);
  };

  const incrementQuantity = () => {
    setEditingQuantity((prev) => prev + 1);
  };

  const decrementQuantity = () => {
    setEditingQuantity((prev) => Math.max(1, prev - 1));
  };

  // Group items by comboId and orderComboElement.group
  const groupedItems = cart.items.reduce(
    (acc, item) => {
      // Skip items that don't belong to a combo
      if (item.comboId === undefined || item.comboId === null) {
        return acc;
      }

      const comboId = item.comboId;
      const group = item.orderComboElement?.group;
      const groupId = `${comboId}-${group}`;

      if (!acc[groupId]) {
        acc[groupId] = {
          comboId,
          group,
          comboName: item.orderComboElement?.comboElement?.combo?.name,
          items: [],
          extras: [],
        };
      }

      acc[groupId].items.push(item);
      return acc;
    },
    {} as Record<
      string,
      {
        comboId: number;
        group: number;
        comboName: string;
        items: typeof cart.items;
        extras: typeof cart.extras;
      }
    >
  );

  // Add extras to each group
  cart.extras.forEach((extra) => {
    if (!extra.parentOrderElementId) return;

    // Find which group this extra belongs to
    Object.values(groupedItems).forEach((group) => {
      const itemIds = group.items.map((item) => item.id);
      if (itemIds.includes(extra.parentOrderElementId)) {
        group.extras.push(extra);
      }
    });
  });

  // Convert to array and add combo data
  const groupedItemsArray = Object.values(groupedItems).map((group) => {
    return {
      ...group,
      combo: cart.combos[group.comboId],
    };
  });

  return (
    <DrawerContent className="flex flex-col h-[85vh] max-h-[85vh]">
      <DrawerHeader className="flex-shrink-0">
        <DrawerTitle>{"Panier"}</DrawerTitle>
      </DrawerHeader>

      <div className="flex-1 overflow-y-auto px-4">
        <div className="grid gap-2 grid-cols-1 xl:grid-cols-3 2xl:grid-cols-4">
          {/* Render grouped combo items with their extras */}
          {groupedItemsArray.map((group, index) => {
            return (
              <div
                key={`group-${group.comboId}-${group.group}-${index}`}
                className="border rounded-md p-3 mb-2 bg-secondary"
              >
                <div className="flex justify-between mb-2">
                  <div className="font-medium">
                    {group.comboName && `Formule «${group.comboName}»`}
                  </div>
                  <Button
                    variant={"link"}
                    className="h-4 w-4 px-0 text-xs text-destructive"
                    onClick={() => {
                      // Get all variant IDs from this combo group
                      const variantIds = group.items
                        .filter((item) => item && item.variant)
                        .map((item) => item.variant.id);

                      // Use the group ID as the combo key
                      handleRemoveCombo(group.comboId, variantIds);
                    }}
                    disabled={cart.isPaid || isReadOnly}
                  >
                    <Trash2
                      className={cart.isPaid || isReadOnly ? "opacity-50" : ""}
                    />
                  </Button>
                </div>
                {group.items.map((item) => (
                  <div key={item.id} className="mb-2">
                    <Card className="p-2 bg-background">
                      <div className="flex justify-between">
                        <div>
                          <p className="font-medium">
                            {item.quantity} × {item.name} {item.variant.name}
                          </p>
                        </div>
                      </div>
                    </Card>

                    {/* Render extras belonging to this combo item */}
                    {group.extras.filter(
                      (extra) => extra.parentOrderElementId === item.id
                    ).length > 0 && (
                      <div className="ml-4 mt-1 space-y-1">
                        {group.extras
                          .filter(
                            (extra) => extra.parentOrderElementId === item.id
                          )
                          .map((extra) => (
                            <div
                              key={extra.id}
                              className="flex justify-between text-sm"
                            >
                              <div className="flex items-center">
                                <span className="font-medium">
                                  {extra.extra.name}
                                </span>
                                <span className="ml-1 text-muted-foreground">
                                  x{extra.quantity}
                                </span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <span>
                                  {Intl.NumberFormat("fr-FR", {
                                    style: "currency",
                                    currency: "EUR",
                                  }).format(
                                    extra.quantity *
                                      Number(extra.extra.priceAfterVat)
                                  )}
                                </span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-5 px-1 text-xs text-destructive"
                                  onClick={handleRemoveExtra(extra.id)}
                                  disabled={cart.isPaid || isReadOnly}
                                >
                                  {"Supprimer"}
                                </Button>
                              </div>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            );
          })}

          {/* Render non-combo items */}
          {orderBy(
            cart.items.filter((item) => !item.comboId),
            ["name", "asc"]
          ).map((item) => {
            // Get extras that belong to this item
            const itemExtras = cart.extras.filter(
              (extra) => extra.parentOrderElementId === item.id
            );

            return (
              <div key={item.id}>
                <Card className="p-2 bg-background">
                  <div className="flex justify-between">
                    <div>
                      {editingItemId === item.id ? (
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={decrementQuantity}
                            disabled={cart.isPaid || isReadOnly}
                          >
                            <Minus className="h-3 w-3" />
                          </Button>
                          <span className="font-medium">{editingQuantity}</span>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={incrementQuantity}
                            disabled={cart.isPaid || isReadOnly}
                          >
                            <Plus className="h-3 w-3" />
                          </Button>
                          <span className="ml-2">
                            × {item.name} {item.variant.name}
                          </span>
                        </div>
                      ) : (
                        <p className="font-medium">
                          {item.quantity} × {item.name} {item.variant.name}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex justify-between">
                    {item.quantity * Number(item.variant.priceAfterVat) ===
                    Number(item.amountAfterVat) ? (
                      <p>
                        {Intl.NumberFormat("fr-FR", {
                          style: "currency",
                          currency: "EUR",
                        }).format(
                          item.quantity * Number(item.variant.priceAfterVat)
                        )}
                      </p>
                    ) : (
                      <>
                        <p className="space-x-2">
                          <span className="line-through">
                            {Intl.NumberFormat("fr-FR", {
                              style: "currency",
                              currency: "EUR",
                            }).format(
                              item.quantity * Number(item.variant.priceAfterVat)
                            )}
                          </span>

                          <span className="text-primary">
                            {Intl.NumberFormat("fr-FR", {
                              style: "currency",
                              currency: "EUR",
                            }).format(Number(item.amountAfterVat))}
                          </span>
                        </p>
                      </>
                    )}

                    <div className="flex items-center space-x-2">
                      {editingItemId === item.id ? (
                        <Button
                          variant={"link"}
                          className="h-4 w-4 px-0"
                          onClick={() => updateItemQuantity(item)}
                          disabled={cart.isPaid || isReadOnly}
                        >
                          <Check
                            className={
                              cart.isPaid || isReadOnly ? "opacity-50" : ""
                            }
                          />
                        </Button>
                      ) : (
                        <Button
                          variant={"link"}
                          className="h-4 w-4 px-0 text-card-foreground"
                          onClick={() => startEditingItem(item)}
                          disabled={cart.isPaid || isReadOnly}
                        >
                          <Pen
                            className={
                              cart.isPaid || isReadOnly ? "opacity-50" : ""
                            }
                          />
                        </Button>
                      )}
                      <Button
                        variant={"link"}
                        className="h-4 w-4 px-0 text-xs text-destructive"
                        onClick={() => onClickRemove?.(item.variant.id)}
                        disabled={cart.isPaid || isReadOnly}
                      >
                        <Trash2
                          className={
                            cart.isPaid || isReadOnly ? "opacity-50" : ""
                          }
                        />
                      </Button>
                    </div>
                  </div>
                </Card>

                {/* Render extras belonging to this item */}
                {itemExtras.length > 0 && (
                  <div className="ml-4 mt-1 space-y-1">
                    {itemExtras.map((extra) => (
                      <div
                        key={extra.id}
                        className="flex justify-between text-sm"
                      >
                        <div className="flex items-center">
                          <span className="font-medium">
                            {extra.extra.name}
                          </span>
                          <span className="ml-1 text-muted-foreground">
                            x{extra.quantity}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span>
                            {Intl.NumberFormat("fr-FR", {
                              style: "currency",
                              currency: "EUR",
                            }).format(
                              extra.quantity * Number(extra.extra.priceAfterVat)
                            )}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-5 px-1 text-xs text-destructive"
                            onClick={handleRemoveExtra(extra.id)}
                            disabled={cart.isPaid || isReadOnly}
                          >
                            {"Supprimer"}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {/* Render standalone extras (those without a parent item) */}
          {orderBy(
            cart.extras.filter((item) => !item.parentOrderElementId),
            ["name", "asc"]
          ).map((item) => {
            return (
              <div key={item.id}>
                <div className="flex justify-between">
                  <h4 className="font-semibold">{item.extra.name}</h4>
                  <p>
                    {Intl.NumberFormat("fr-FR", {
                      style: "currency",
                      currency: "EUR",
                    }).format(item.quantity * Number(item.extra.priceAfterVat))}
                  </p>
                </div>
                <div className="flex justify-between items-center">
                  <p>
                    {item.quantity}{" "}
                    {item.extra.name && pluralize("supplément", item.quantity)}
                  </p>
                  <Button
                    variant={"link"}
                    className="h-5 px-0 text-xs text-destructive"
                    onClick={handleRemoveExtra(item.id)}
                    disabled={cart.isPaid || isReadOnly}
                  >
                    {"Supprimer"}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex space-x-2 justify-end items-end">
          <h4 className="text-xs">{"Total"}</h4>
          <p>
            {Intl.NumberFormat("fr-FR", {
              style: "currency",
              currency: "EUR",
            }).format(cart.totalAfterVat)}
          </p>
        </div>

        {cart.totalPromotion > 0 && (
          <div className="flex text-primary space-x-2 justify-end items-end">
            <h4 className="text-xs">{"Promotion"}</h4>
            <p>
              {Intl.NumberFormat("fr-FR", {
                style: "currency",
                currency: "EUR",
              }).format(cart.totalPromotion)}
            </p>
          </div>
        )}

        {cart.totalPromotion > 0 && (
          <div className="flex space-x-2 justify-end items-end mb-4">
            <h4 className="text-xs">{"Total après promotion"}</h4>
            <p>
              {Intl.NumberFormat("fr-FR", {
                style: "currency",
                currency: "EUR",
              }).format(cart.totalAfterVat - cart.totalPromotion)}
            </p>
          </div>
        )}
      </div>

      {!cart.isPaid && (
        <DrawerFooter className="flex-shrink-0">
          <div className="flex justify-between">
            <Button variant="destructive" onClick={handleClickEmptyCart}>
              {"Vider le panier"}
            </Button>
            <Button onClick={onClickNext}>{"Suivant"}</Button>
          </div>
        </DrawerFooter>
      )}
    </DrawerContent>
  );
};

export default observer(CustomerCartDrawer);
